import { normalizedSiteName, reportBelongsToSite } from './reportSiteIdentity';
/**
 * siteReportLink — the canonical "does this site have any reports?" join.
 *
 * A report can be linked to a site three ways (the platform's data model
 * accreted them over time):
 *   1. `site.reportIds.includes(report.id)` — the legacy on-site array,
 *      maintained by older / wizard-created paths.
 *   2. `report.siteId === site.id` — modern reports carry a back-pointer.
 *   3. `report.siteName === site.name` (case- and whitespace-insensitive)
 *      — pre-`siteId` reports that only know the site by name.
 *
 * Several surfaces already do this triple join inline (siteRagService's
 * readiness recompute, siteSummaryScore, …). The Dashboard's
 * "Sites without reports" filter historically checked only (1), so any
 * site whose reports are linked via (2) or (3) — but whose `reportIds`
 * array drifted stale — fell through to the zero-report bucket. This
 * module is the shared implementation so that bug can't recur per-site.
 */
import type { SiteEntity } from '../types/site';
import type { SiteRagReport } from '../types/siteRag';
import { decodeFirestoreNestedArrays } from './generationImpact/firestoreNestedArrayCodec';

/** Normalise a site / report name for case- and whitespace-insensitive comparison. */
export function normalizeSiteName(name: string | null | undefined): string {
  return normalizedSiteName(name);
}

/**
 * Normalise a raw Firestore site doc into a runtime-safe `SiteEntity`.
 *
 * `SiteEntity.reportIds` / `simulationIds` / `documentIds` are typed
 * `string[]` (required), but legacy site docs predate these fields, so at
 * runtime they arrive `undefined`. Admins load sites across the whole company
 * subtree, so they reliably hit a legacy doc and any unguarded
 * `site.reportIds.includes(...)` throws
 * `Cannot read properties of undefined (reading 'includes')`.
 *
 * This is the single source-normalization point: every site-doc → in-memory
 * `SiteEntity` mapping (own sites + company-shared collectionGroup sites in
 * `fetchSites`) routes through here so the type's promise is true at runtime
 * and downstream `.includes`/`.length`/`.map` over these arrays is safe.
 *
 * Defensive call-site guards (`(site.reportIds ?? [])`) remain as
 * belt-and-suspenders for any site object constructed outside this path.
 *
 * It is also the client's ONE decode point for
 * `promotedGenerationScenarioSnapshot`: Firestore cannot store an array inside
 * an array, so the promoted scenario's exclusion polygons and noise rings are
 * persisted wrapped. Only that field is decoded — every other field must keep
 * its persisted shape and identity.
 *
 * COST — call this at Firestore READ BOUNDARIES, once per site doc; never in a
 * render path, a per-keystroke `useMemo`, or a list callback. A site with no
 * promoted embed skips the decode entirely, but when the embed is present
 * `decodeFirestoreNestedArrays` walks the WHOLE snapshot — every array element
 * and every plain-object value, to the full depth of the promoted scenario's
 * layout, exclusion polygons and noise field. What it saves in the unwrapped
 * case is ALLOCATION, not traversal: it returns the snapshot by reference when
 * nothing was wrapped, so no copy of the body is made. `normalizeSiteEntity`
 * itself always allocates — it spreads `site` on every call.
 */
export function normalizeSiteEntity<T extends Partial<SiteEntity>>(site: T): T & {
  reportIds: string[];
  simulationIds: string[];
  documentIds: string[];
} {
  const promoted = site.promotedGenerationScenarioSnapshot;
  const decodedPromoted = promoted ? decodeFirestoreNestedArrays(promoted) : promoted;
  return {
    ...site,
    ...(decodedPromoted === promoted
      ? null
      : { promotedGenerationScenarioSnapshot: decodedPromoted }),
    reportIds: Array.isArray(site.reportIds) ? site.reportIds : [],
    simulationIds: Array.isArray(site.simulationIds) ? site.simulationIds : [],
    documentIds: Array.isArray(site.documentIds) ? site.documentIds : [],
  };
}

/**
 * Every report linked to `site` by any of the three join paths.
 * De-duplicated by report id (a report can match on more than one path).
 */
export function reportsForSite(site: SiteEntity, allReports: readonly SiteRagReport[]): SiteRagReport[] {
  const seen = new Set<string>();
  const out: SiteRagReport[] = [];
  for (const r of allReports) {
    const linked = reportBelongsToSite(site, r);
    if (linked && !seen.has(r.id)) {
      seen.add(r.id);
      out.push(r);
    }
  }
  return out;
}

/**
 * True when `site` has at least one report linked by any path. Fast-paths
 * the common case (`reportIds` non-empty) before scanning `allReports`.
 */
export function siteHasReports(
  site: Pick<SiteEntity, 'id' | 'name' | 'reportIds'>,
  allReports: readonly Pick<SiteRagReport, 'id' | 'siteId' | 'siteName'>[],
): boolean {
  if ((site.reportIds?.length ?? 0) > 0) return true;
  return allReports.some(report => reportBelongsToSite(site, report));
}
